import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router';
import type { Role } from '../api/types';
import { useMe, useMembership } from '../auth/session';
import ui from '../styles/ui.module.css';

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className={ui.page}>
      <p className={ui.muted}>{t('common.loading')}</p>
    </div>
  );
}

export function ErrorView({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={ui.page}>
      <div className={`${ui.notice} ${ui.noticeError}`}>
        {t('common.error')}{' '}
        {onRetry && (
          <button type="button" className={ui.linkButton} onClick={onRetry}>
            {t('common.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

export function Forbidden() {
  const { t } = useTranslation();
  return (
    <div className={ui.page}>
      <div className={ui.card}>
        <p>{t('common.forbidden')}</p>
        <Link to="/">{t('common.backHome')}</Link>
      </div>
    </div>
  );
}

/** Renders children only for a signed-in user; otherwise goes to the login page. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <Loading />;
  if (me.isError) return <ErrorView onRetry={() => void me.refetch()} />;
  if (!me.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** Renders children only if the user holds one of the roles in their choir. */
export function RequireRole({ anyOf, children }: { anyOf: Role[]; children: ReactNode }) {
  const membership = useMembership();
  if (!membership || !anyOf.some((r) => membership.roles.includes(r))) return <Forbidden />;
  return <>{children}</>;
}

/**
 * Catches a page that fails to render (e.g. unexpected data) and shows the error notice instead
 * of a blank screen; the header stays usable. `resetKey` (the location) clears it on navigation.
 */
export class PageErrorBoundary extends Component<{ resetKey: string; children: ReactNode }, { failed: boolean; key: string }> {
  state = { failed: false, key: this.props.resetKey };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  static getDerivedStateFromProps(props: { resetKey: string }, state: { failed: boolean; key: string }) {
    return props.resetKey !== state.key ? { failed: false, key: props.resetKey } : null;
  }

  componentDidCatch(error: unknown) {
    console.error('Page failed to render', error);
  }

  render() {
    return this.state.failed ? <ErrorView onRetry={() => this.setState({ failed: false })} /> : this.props.children;
  }
}
